/**
 * InlinePropertyEditor - Inline property definitions for entity type creation
 *
 * Provides a repeating list of expandable property forms that can be added
 * to a new entity type without navigating to a separate panel.
 * Follows the same field types and naming conventions as PropertyTypeSlideout.
 */

import { ChevronDown, ChevronRight, Plus, Trash01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { Input } from '@/lib/base/forms/input/input';
import { Label } from '@/lib/base/forms/input/label';
import { Select } from '@/lib/base/forms/select/select';
import type { SelectOption } from '@/lib/crm/components/properties/types';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import {
  generatePropertyName,
  getFieldTypeItems,
  resolveNameCollision,
} from '@/lib/crm/utils/fieldTypeHelpers';
import { SelectOptionsEditor } from '@/pages/dashboard/profile/components/SelectOptionsEditor';

// =============================================================================
// Types
// =============================================================================

export interface PropertyDefinition {
  /** Unique client-side ID */
  id: string;
  /** User-visible label */
  label: string;
  /** Auto-generated property name (org_ prefixed) */
  name: string;
  /** Field type (text, number, select, etc.) */
  fieldType: string;
  /** Optional description */
  description: string;
  /** Whether the property enforces unique values */
  isUnique: boolean;
  /** Options for select field type */
  options: SelectOption[];
}

export interface InlinePropertyEditorProps {
  /** Current list of property definitions */
  properties: PropertyDefinition[];
  /** Callback when properties change */
  onChange: (properties: PropertyDefinition[]) => void;
  /** Whether the editor is disabled (e.g. during save) */
  disabled?: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

/** Create a blank property definition. */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with InlinePropertyEditor component
export function createBlankProperty(): PropertyDefinition {
  return {
    id: crypto.randomUUID(),
    label: '',
    name: '',
    fieldType: 'text',
    description: '',
    isUnique: false,
    options: [],
  };
}

// =============================================================================
// PropertyCard - Single expandable property form
// =============================================================================

interface PropertyCardProps {
  property: PropertyDefinition;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (updated: PropertyDefinition) => void;
  onRemove: () => void;
  disabled: boolean;
  fieldTypeItems: ReturnType<typeof getFieldTypeItems>;
  fieldTypesLoading: boolean;
  error?: string;
}

function PropertyCard({
  property,
  index,
  isExpanded,
  onToggle,
  onChange,
  onRemove,
  disabled,
  fieldTypeItems,
  fieldTypesLoading,
  error,
}: PropertyCardProps) {
  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;
  const fieldTypeLabel =
    fieldTypeItems.find(i => i.id === property.fieldType)?.label ??
    property.fieldType;
  const displayLabel = property.label.trim() || `Property ${index + 1}`;

  const updateField = useCallback(
    <K extends keyof PropertyDefinition>(
      field: K,
      value: PropertyDefinition[K]
    ) => {
      onChange({ ...property, [field]: value });
    },
    [property, onChange]
  );

  // When label changes, auto-generate name
  const handleLabelChange = useCallback(
    (newLabel: string) => {
      const newName = generatePropertyName(newLabel);
      onChange({ ...property, label: newLabel, name: newName });
    },
    [property, onChange]
  );

  // When field type changes, clear options if moving away from select
  const handleFieldTypeChange = useCallback(
    (newType: string) => {
      const updates: Partial<PropertyDefinition> = { fieldType: newType };
      if (newType !== 'select') {
        updates.options = [];
      }
      onChange({ ...property, ...updates });
    },
    [property, onChange]
  );

  return (
    <div className='border border-border-primary rounded-lg overflow-hidden'>
      {/* Header - always visible */}
      <button
        type='button'
        onClick={onToggle}
        disabled={disabled}
        className='w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-bg-secondary transition-colors'
      >
        <ChevronIcon className='w-4 h-4 text-text-tertiary flex-shrink-0' />
        <span className='text-sm font-medium text-text-primary truncate flex-1'>
          {displayLabel}
        </span>
        <span className='text-xs text-text-tertiary flex-shrink-0'>
          {fieldTypeLabel}
        </span>
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <div className='px-3 pb-3 space-y-4 border-t border-border-primary pt-3'>
          {/* Error */}
          {error && <p className='text-sm text-text-error-primary'>{error}</p>}

          {/* Label */}
          <Input
            label='Label'
            placeholder='e.g. Priority'
            value={property.label}
            onChange={handleLabelChange}
            isDisabled={disabled}
            isRequired
          />

          {/* Field Type */}
          <Select
            label='Field type'
            placeholder={fieldTypesLoading ? 'Loading…' : 'Select field type'}
            items={fieldTypeItems}
            selectedKey={property.fieldType}
            onSelectionChange={key => handleFieldTypeChange(key as string)}
            isRequired
            isDisabled={disabled || fieldTypesLoading}
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>

          {/* Description */}
          <div className='space-y-1.5'>
            <Label>Description</Label>
            <textarea
              placeholder='Optional description'
              value={property.description}
              onChange={e => updateField('description', e.target.value)}
              rows={2}
              disabled={disabled}
              className='w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-primary placeholder:text-tertiary focus:border-brand-solid focus:outline-none focus:ring-1 focus:ring-brand-solid resize-none'
            />
          </div>

          {/* Options for select type */}
          {property.fieldType === 'select' && (
            <SelectOptionsEditor
              options={property.options}
              onChange={options => updateField('options', options)}
              disabled={disabled}
            />
          )}

          {/* Unique checkbox */}
          <Checkbox
            label='Require unique values'
            hint='Each record must have a different value for this property.'
            isSelected={property.isUnique}
            onChange={selected => updateField('isUnique', selected)}
            isDisabled={disabled}
          />

          {/* Remove button */}
          <div className='pt-1'>
            <Button
              size='sm'
              color='secondary-destructive'
              iconLeading={Trash01}
              onClick={onRemove}
              isDisabled={disabled}
            >
              Remove property
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// InlinePropertyEditor - Main component
// =============================================================================

export function InlinePropertyEditor({
  properties,
  onChange,
  disabled = false,
}: InlinePropertyEditorProps) {
  const { data: fieldTypes = [], isLoading: fieldTypesLoading } =
    useFieldTypes();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const fieldTypeItems = useMemo(
    () => getFieldTypeItems(fieldTypes),
    [fieldTypes]
  );

  // Resolve name collisions across all properties
  const resolvedProperties = useMemo(() => {
    const usedNames = new Set<string>();
    return properties.map(prop => {
      if (!prop.name) {
        return prop;
      }
      const resolved = resolveNameCollision(prop.name, usedNames);
      usedNames.add(resolved);
      return resolved !== prop.name ? { ...prop, name: resolved } : prop;
    });
  }, [properties]);

  // Sync resolved names back to stored state
  useEffect(() => {
    const hasChanges = resolvedProperties.some(
      (resolved, idx) => resolved.name !== properties[idx].name
    );
    if (hasChanges) {
      onChange(resolvedProperties);
    }
  }, [resolvedProperties, properties, onChange]);

  // Validation errors
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const labelSet = new Set<string>();
    properties.forEach(prop => {
      if (!prop.label.trim()) {
        errors[prop.id] = 'Label is required';
      } else if (!prop.name) {
        errors[prop.id] = 'Label must contain at least one letter or number';
      } else if (labelSet.has(prop.label.toLowerCase().trim())) {
        errors[prop.id] = 'Duplicate property label';
      }
      labelSet.add(prop.label.toLowerCase().trim());

      if (prop.fieldType === 'select' && prop.options.length < 2) {
        errors[prop.id] =
          errors[prop.id] || 'Add 2 or more options to save changes.';
      }
    });
    return errors;
  }, [properties]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    const newProp = createBlankProperty();
    onChange([...properties, newProp]);
    // Auto-expand newly added property
    setExpandedIds(prev => new Set([...prev, newProp.id]));
  }, [properties, onChange]);

  const handleChange = useCallback(
    (index: number, updated: PropertyDefinition) => {
      const next = [...properties];
      next[index] = updated;
      onChange(next);
    },
    [properties, onChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const removed = properties[index];
      onChange(properties.filter((_, i) => i !== index));
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.delete(removed.id);
        return next;
      });
    },
    [properties, onChange]
  );

  return (
    <div>
      <label className='block text-sm font-medium text-text-primary mb-2'>
        Properties
      </label>

      {properties.length === 0 ? (
        <div className='py-3 px-3 text-center border border-dashed border-border-secondary rounded-lg mb-3'>
          <p className='text-sm text-text-tertiary'>
            No properties yet. Add properties to define what data this object
            stores.
          </p>
        </div>
      ) : (
        <div className='space-y-2 mb-3'>
          {resolvedProperties.map((prop, index) => (
            <PropertyCard
              key={prop.id}
              property={prop}
              index={index}
              isExpanded={expandedIds.has(prop.id)}
              onToggle={() => toggleExpanded(prop.id)}
              onChange={updated => handleChange(index, updated)}
              onRemove={() => handleRemove(index)}
              disabled={disabled}
              fieldTypeItems={fieldTypeItems}
              fieldTypesLoading={fieldTypesLoading}
              error={validationErrors[prop.id]}
            />
          ))}
        </div>
      )}

      <Button
        size='sm'
        color='secondary'
        iconLeading={Plus}
        onClick={handleAdd}
        isDisabled={disabled}
      >
        Add property
      </Button>
    </div>
  );
}

export { type SelectOption };
